import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { ZonedDateTime, fromDate } from '@internationalized/date';
import { cn } from '@antimatterhq/ui/lib/utils';
import { useAtomValue } from 'jotai';
import { getHistogramBarInterval } from '../histogram.utils';
import {
  histogramTimelineDragOffsetAtom,
  histogramVisibleTimeRangeAtom,
} from '../jotai/histogramAtoms';
import {
  calculateSelectionBounds,
  findBarFromMousePosition,
  getUnloadedRegions,
  paintBars,
} from './histogramBars.utils';
import { HistogramBarsUnloadedRegion } from './unloadedRegion/histogramBarsUnloadedRegion';

type HistogramDataPoint = {
  startTime: ZonedDateTime;
  endTime: ZonedDateTime;
  value: number;
  metadata?: Record<string, unknown>;
};

type HistogramBarsProps = {
  data?: HistogramDataPoint[];
  dragOffset?: number;
  maxBarHeight?: number;
  barColor?: string;
  className?: string;
  maxValue?: number;
  loadedTimeRanges?: [ZonedDateTime, ZonedDateTime][];
  onBarHover?: (bar: HistogramDataPoint | null, x: number, y: number) => void;
  onSelectionEnd?: (startTime: ZonedDateTime, endTime: ZonedDateTime) => void;
  onLoadData?: (startTime: ZonedDateTime, endTime: ZonedDateTime) => void;
  onDatetimeSelect?: (datetime: ZonedDateTime) => void;
};

const HistogramBars = ({
  data,
  maxBarHeight = 40,
  barColor = '#60a5fa',
  className,
  maxValue,
  loadedTimeRanges = [],
  onBarHover,
  onSelectionEnd,
  onLoadData,
  onDatetimeSelect,
}: HistogramBarsProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredBar, setHoveredBar] = useState<HistogramDataPoint | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [startTime, endTime] = useAtomValue(histogramVisibleTimeRangeAtom);
  const dragOffset = useAtomValue(histogramTimelineDragOffsetAtom);

  // Track container width for reliable calculations
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    // Update width on mount and resize
    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  // Calculate unloaded regions for background rendering
  const unloadedRegions = useMemo(
    () => getUnloadedRegions(loadedTimeRanges, startTime),
    [loadedTimeRanges, startTime],
  );

  // Helper function to check if a bar's time range falls within loaded ranges
  const isBarLoaded = useCallback(
    (barStart: ZonedDateTime, barEnd: ZonedDateTime): boolean => {
      return loadedTimeRanges.some(
        ([loadedStart, loadedEnd]) =>
          barStart.compare(loadedStart) >= 0 && barEnd.compare(loadedEnd) <= 0,
      );
    },
    [loadedTimeRanges],
  );

  // Selection state - now using time-based selection instead of index-based
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStartTime, setSelectionStartTime] =
    useState<ZonedDateTime | null>(null);
  const [selectionEndTime, setSelectionEndTime] =
    useState<ZonedDateTime | null>(null);
  const selectionStartX = useRef(0);
  const [hasDragged, setHasDragged] = useState(false);
  const dragThreshold = 5; // pixels - minimum movement to consider it a drag

  const renderBars = useCallback(
    () =>
      paintBars(
        canvasRef,
        endTime,
        startTime,
        dragOffset,
        data,
        maxValue,
        maxBarHeight,
        hoveredBar,
        barColor,
      ),
    [
      canvasRef,
      endTime,
      startTime,
      dragOffset,
      data,
      maxValue,
      maxBarHeight,
      hoveredBar,
      barColor,
    ],
  );

  // Helper function to convert mouse X position to time
  const getTimeFromMouseX = useCallback(
    (mouseX: number): ZonedDateTime | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;

      const rect = canvas.getBoundingClientRect();
      const canvasX = mouseX - rect.left;
      const canvasWidth = rect.width;

      // Calculate time from position
      const visibleDurationMs =
        endTime.toDate().getTime() - startTime.toDate().getTime();
      const pixelsPerMs = canvasWidth / visibleDurationMs;
      const timeOffsetMs = (canvasX - dragOffset) / pixelsPerMs;

      return startTime.add({ milliseconds: timeOffsetMs });
    },
    [startTime, endTime, dragOffset],
  );

  // Helper function to get bar data from mouse position
  const getBarFromMousePosition = useCallback(
    (mouseX: number, mouseY: number) =>
      findBarFromMousePosition(
        mouseX,
        mouseY,
        canvasRef,
        endTime,
        startTime,
        dragOffset,
        data,
        maxValue,
        maxBarHeight,
        isBarLoaded,
      ),
    [data, startTime, endTime, dragOffset, isBarLoaded, maxValue, maxBarHeight],
  );

  // Mouse event handlers for selection
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const time = getTimeFromMouseX(e.clientX);
      if (time) {
        setIsSelecting(true);
        setSelectionStartTime(time);
        setSelectionEndTime(time);
        setHasDragged(false); // Reset drag state
        selectionStartX.current = e.clientX;
      }
    },
    [getTimeFromMouseX],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const { bar, barCenterX } = getBarFromMousePosition(e.clientX, e.clientY);

      // Handle hover - compare by time values instead of object reference
      const isNewBar =
        (!bar && hoveredBar) ||
        (bar && !hoveredBar) ||
        (bar &&
          hoveredBar &&
          (bar.startTime.toDate().getTime() !==
            hoveredBar.startTime.toDate().getTime() ||
            bar.endTime.toDate().getTime() !==
              hoveredBar.endTime.toDate().getTime()));

      if (isNewBar) {
        setHoveredBar(bar);
        if (onBarHover && canvas) {
          const rect = canvas.getBoundingClientRect();
          // Use bar center X if available, otherwise mouse X
          const xPos = barCenterX ?? e.clientX;
          onBarHover(bar, xPos, rect.top);
        }
      }

      // Handle selection drag
      if (isSelecting) {
        // Check if we've moved enough to consider it a drag
        const deltaX = Math.abs(e.clientX - selectionStartX.current);
        if (deltaX > dragThreshold && !hasDragged) {
          setHasDragged(true);
        }

        const time = getTimeFromMouseX(e.clientX);
        if (time) {
          setSelectionEndTime(time);
        }
      }
    },
    [
      isSelecting,
      getBarFromMousePosition,
      hoveredBar,
      onBarHover,
      getTimeFromMouseX,
      hasDragged,
      dragThreshold,
    ],
  );

  const handleMouseUp = useCallback(() => {
    if (isSelecting && selectionStartTime && selectionEndTime) {
      const canvas = canvasRef.current;
      if (!canvas) {
        setIsSelecting(false);
        setSelectionStartTime(null);
        setSelectionEndTime(null);
        setHasDragged(false);
        return;
      }

      if (!hasDragged) {
        // This was a click, not a drag - fire datetime select event
        if (onDatetimeSelect) {
          onDatetimeSelect(selectionStartTime);
        }
      } else {
        // This was a drag - handle selection as before
        const visibleDurationMs =
          endTime.toDate().getTime() - startTime.toDate().getTime();
        const intervalMs = getHistogramBarInterval(visibleDurationMs);

        // Get the selected range
        const rangeStartTime =
          selectionStartTime.compare(selectionEndTime) < 0
            ? selectionStartTime
            : selectionEndTime;
        const rangeEndTime =
          selectionStartTime.compare(selectionEndTime) < 0
            ? selectionEndTime
            : selectionStartTime;

        // Snap to interval boundaries
        const alignedStartMs =
          Math.floor(rangeStartTime.toDate().getTime() / intervalMs) *
          intervalMs;
        const alignedEndMs =
          Math.ceil(rangeEndTime.toDate().getTime() / intervalMs) * intervalMs;

        // Only trigger if there's a meaningful selection
        if (alignedStartMs !== alignedEndMs) {
          const alignedStartZdt = fromDate(
            new Date(alignedStartMs),
            startTime.timeZone,
          );
          const alignedEndZdt = fromDate(
            new Date(alignedEndMs),
            startTime.timeZone,
          );
          onSelectionEnd?.(alignedStartZdt, alignedEndZdt);
        }
      }

      setIsSelecting(false);
      setSelectionStartTime(null);
      setSelectionEndTime(null);
      setHasDragged(false);
    }
  }, [
    isSelecting,
    selectionStartTime,
    selectionEndTime,
    hasDragged,
    onSelectionEnd,
    onDatetimeSelect,
    startTime,
    endTime,
  ]);

  const handleMouseLeave = useCallback(() => {
    if (isSelecting) {
      setIsSelecting(false);
      setSelectionStartTime(null);
      setSelectionEndTime(null);
      setHasDragged(false);
    }
    setHoveredBar(null);
    if (onBarHover) {
      onBarHover(null, 0, 0);
    }
  }, [isSelecting, onBarHover]);

  // Create a ref to hold the latest renderBars function
  const renderBarsRef = useRef(renderBars);
  renderBarsRef.current = renderBars;

  // Update canvas size when container resizes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeObserver = new ResizeObserver(() => {
      renderBarsRef.current();
    });

    resizeObserver.observe(canvas.parentElement!);

    return () => resizeObserver.disconnect();
  }, []); // Empty dependency array - only set up once

  // Re-render when props change
  useEffect(() => {
    renderBars();
  }, [renderBars]);

  // Warn if data exists outside loaded ranges
  useEffect(() => {
    if (data && loadedTimeRanges.length > 0) {
      const unloadedData = data.filter(
        (d) => !isBarLoaded(d.startTime, d.endTime),
      );
      if (unloadedData.length > 0) {
        console.error(
          `HistogramBars: ${unloadedData.length} data points exist outside loadedTimeRanges. ` +
            'Data points should always be within loaded ranges.',
          {
            unloadedDataPoints: unloadedData.slice(0, 5), // Show first 5 as examples
            loadedTimeRanges,
          },
        );
      }
    }
  }, [data, loadedTimeRanges, isBarLoaded]);

  // Calculate selection highlight bounds
  const selectionBounds = useMemo(
    () =>
      calculateSelectionBounds(
        isSelecting,
        selectionStartTime,
        selectionEndTime,
        canvasRef,
        endTime,
        startTime,
        dragOffset,
      ),
    [
      isSelecting,
      selectionStartTime,
      selectionEndTime,
      startTime,
      endTime,
      dragOffset,
    ],
  );

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full overflow-hidden', className)}
      style={{ height: maxBarHeight }}
    >
      {/* Grey background for unloaded regions - lowest layer */}
      {containerWidth > 0 &&
        unloadedRegions.map((region, index) => (
          <HistogramBarsUnloadedRegion
            key={index}
            endTime={endTime}
            startTime={startTime}
            containerWidth={containerWidth}
            region={region}
            dragOffset={dragOffset}
            onLoadData={onLoadData}
            maxBarHeight={maxBarHeight}
            index={index}
          />
        ))}

      {/* Selection highlight - middle layer */}
      {selectionBounds && (
        <div
          className="pointer-events-none absolute top-0 z-histogramHighlight bg-grey-100 dark:bg-grey-650"
          style={{
            left: `${selectionBounds.left}px`,
            width: `${selectionBounds.width}px`,
            height: maxBarHeight,
          }}
        />
      )}

      {/* Canvas for histogram bars - top layer */}
      <canvas
        ref={canvasRef}
        className="relative z-histogramCanvas h-full w-full cursor-crosshair"
        style={{
          width: '100%',
          height: maxBarHeight,
          display: 'block',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      />
    </div>
  );
};

export { HistogramBars, type HistogramDataPoint };
